import MomentUtils from '@date-io/moment';
import { Box, Button, Card, CardContent, CircularProgress, FormLabel, Grid, TextField } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment, { Moment } from 'moment-timezone';
import React, { useState } from 'react';
import useStyles from './styles';
import { IRedeemCode } from '../../../services/types';
import { createRedeemCode, updateRedeemCodeById } from '../../../services/api/RedeemCode';

interface IProps {
  closeCardFunction?: (state: boolean) => void;
  isEdit: boolean;
  redeemCode?: IRedeemCode;
  successCallback?: () => void;
}

const CreateRedeemCodeCard = (props: IProps): JSX.Element => {
  const isEdit = props.isEdit;
  const redeemCode = props.redeemCode;
  const classes = useStyles();

  const [expiredDate, setExpiredDate] = useState<Moment>(isEdit && redeemCode ? moment.utc(redeemCode.expiredAt) : moment.utc().add(10, 'years'));
  const [title, setTitle] = useState<string>(isEdit && redeemCode ? redeemCode.title : '');
  const [validityPeriod, setValidityPeriod] = useState<null | number>(isEdit && redeemCode ? redeemCode.validityPeriod : 3);
  const [storyId, setStoryId] = useState<null | number>(isEdit && redeemCode ? redeemCode.storyId : null);
  const [count, setCount] = useState<number | null>(isEdit && redeemCode ? redeemCode.count : 1000000);
  const [amount, setAmount] = useState<number | null>(isEdit && redeemCode ? redeemCode.amount : 3);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const createCoupon = async () => {
    try {
      setIsCreating(true);
      await createRedeemCode({
        type: 'story-coupon',
        title,
        validityPeriod: validityPeriod ? validityPeriod : undefined,
        expiredAt: expiredDate.toDate(),
        storyId: storyId ? storyId : undefined,
        count: count ? count : undefined,
        amount: amount ? amount : undefined,
      });
    } catch (e) {
      alert('error occured');
    }
    setIsCreating(false);
    if (props.successCallback) props.successCallback();
  };

  const updateCoupon = async () => {
    try {
      setIsCreating(true);
      if (redeemCode !== undefined) await updateRedeemCodeById(redeemCode.id, title, count as number);
    } catch (e) {
      alert('error occured');
    }
    setIsCreating(false);
    if (props.successCallback) props.successCallback();
  };

  return (
    <Card className={classes.createRoot}>
      <CardContent>
        <form className={classes.createCouponFormContainer} autoComplete="off">
          <TextField
            id="coupon-title"
            label="Coupon Title"
            fullWidth
            value={title}
            onChange={e => {
              setTitle(e.target.value ? e.target.value : '');
            }}
          />
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Validity Period (Days)</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="validity-period"
                  placeholder="Validity Period"
                  fullWidth
                  type="number"
                  disabled={isEdit}
                  value={validityPeriod}
                  onChange={e => {
                    if (e.target.value) setValidityPeriod(parseInt(e.target.value, 10));
                    else setValidityPeriod(null);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Code Expiration Date</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="ddd, MMM D, YYYY"
                    margin="normal"
                    id="date-picker-inline"
                    disabled={isEdit}
                    value={expiredDate}
                    onChange={date => {
                      if (date) setExpiredDate(date);
                    }}
                    KeyboardButtonProps={{
                      'aria-label': 'change date',
                    }}
                  ></KeyboardDatePicker>
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Code</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField id="code" placeholder="Random generated string" fullWidth disabled value={isEdit && redeemCode ? redeemCode.code : ''} />
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Story Id</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="story-id"
                  placeholder=""
                  fullWidth
                  type="number"
                  disabled={isEdit}
                  value={storyId ? storyId : ''}
                  onChange={e => {
                    if (e.target.value) setStoryId(parseInt(e.target.value));
                    else setStoryId(null);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Redeemable count</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="count"
                  placeholder=""
                  fullWidth
                  type="number"
                  value={count ? count : ''}
                  onChange={e => {
                    if (e.target.value) setCount(parseInt(e.target.value));
                    else setCount(null);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1}>
            <Grid container spacing={2} alignItems="flex-end" justify="center">
              <Grid item xs={4}>
                <FormLabel className={classes.createCouponFormLabel}>Number of Coupons</FormLabel>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="amount"
                  placeholder=""
                  fullWidth
                  type="number"
                  disabled={isEdit}
                  value={amount ? amount : ''}
                  onChange={e => {
                    if (e.target.value) setAmount(parseInt(e.target.value));
                    else setAmount(null);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={2} width={1} display="flex" justifyContent="flex-end">
            <Button
              onClick={() => {
                props.closeCardFunction ? props.closeCardFunction(false) : '';
              }}
              className={classes.createCouponCardCancelButton}
            >
              CANCEL
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                if (!isEdit) {
                  createCoupon();
                } else {
                  updateCoupon();
                }
              }}
              disabled={isCreating}
            >
              {!isCreating && (!isEdit ? 'CREATE' : 'SAVE')}
              {isCreating && <CircularProgress className={classes.progress} size={12} color="primary" />}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateRedeemCodeCard;
